import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import Icon from './Icon';
import { BRAND } from '../utils/brand';

// Extra titles for routes that aren't top-level nav entries (detail/sub pages).
const EXTRA_TITLES = { '/': 'Dashboard', '/products': 'Products', '/raw-materials': 'Raw Materials', '/recipes': 'Recipes', '/billing': 'Billing' };
const titleForPath = (path, nav) => {
  for (const n of nav) {
    if (n.to && n.to !== '/' && (path === n.to || path.startsWith(`${n.to}/`))) return n.label;
    for (const it of n.items || []) if (path === it.to || path.startsWith(`${it.to}/`)) return it.label;
  }
  const base = `/${path.split('/')[1] || ''}`;
  return EXTRA_TITLES[path] || EXTRA_TITLES[base] || '';
};

// Nav is a mix of top-level links and grouped sections. `perm` null = always
// visible to a logged-in user. A group renders only if ≥1 child is permitted.
// `tint` colours the item's icon — from the storefront's category accents, so a
// glance down the sidebar reads as areas of the business rather than grey glyphs.
const NAV = [
  { to: '/', label: 'Dashboard', end: true, perm: 'dashboard:read', icon: 'home', tint: 'var(--brand)' },
  { group: 'Sales', items: [
    { to: '/billing', label: 'Billing', perm: 'sales:create', icon: 'cart', tint: 'var(--cat-snacks)' },
    { to: '/sales', label: 'Invoices', perm: 'sales:read', icon: 'receipt', tint: 'var(--cat-ghee)' },
    // Register sessions + end-of-day cash count for a branch.
    { to: '/day-close', label: 'Day Close', perm: 'sales:read', icon: 'clipboard', tint: 'var(--cat-sweets)' },
  ] },
  { group: 'Products', items: [
    { to: '/products', label: 'All Products', perm: 'products:read', icon: 'package', tint: 'var(--cat-pickles)' },
    { to: '/stock-requests', label: 'Stock Requests', perm: 'transfers:read', icon: 'clipboard', tint: '#2a5d9e' },
    // Direct branch-to-branch / warehouse push transfer — no stock request needed.
    { to: '/transfers', label: 'Transfers', perm: 'transfers:write', icon: 'transfer', tint: 'var(--cat-powders)' },
    { to: '/returns', label: 'Stock Returns', perm: 'transfers:read', icon: 'undo', tint: 'var(--cat-sweets)' },
    { to: '/inventory', label: 'Inventory', perm: 'inventory:read', icon: 'layers', tint: 'var(--green)' },
  ] },
  { group: 'Production', items: [
    { to: '/raw-materials', label: 'Raw Materials', perm: 'rawmaterials:read', icon: 'droplet', tint: '#2a5d9e' },
    { to: '/recipes', label: 'Recipes / BOM', perm: 'recipes:read', icon: 'book', tint: 'var(--cat-ghee)' },
    { to: '/production', label: 'Making', perm: 'production:read', icon: 'flame', tint: 'var(--cat-snacks)' },
  ] },
  { group: 'Warehouse', items: [
    { to: '/procurement', label: 'Procurement', perm: 'procurement:read', icon: 'truck', tint: 'var(--cat-powders)' },
    { to: '/packing', label: 'Packing', perm: 'packing:read', icon: 'archive', tint: 'var(--green-2)' },
    { to: '/labels', label: 'Print Labels', perm: 'packing:read', icon: 'tag', tint: 'var(--cat-sweets)' },
    { to: '/suppliers', label: 'Suppliers', perm: 'suppliers:read', icon: 'briefcase', tint: 'var(--maroon)' },
  ] },
  { to: '/reports', label: 'Reports', perm: 'reports:read', icon: 'chart', tint: '#2a5d9e' },
  { group: 'Team', items: [
    { to: '/employees', label: 'Employees', perm: 'employees:read', icon: 'users', tint: '#2a5d9e' },
    { to: '/attendance', label: 'Attendance', perm: 'employees:read', icon: 'clipboard', tint: 'var(--cat-pickles)' },
    { to: '/advances', label: 'Advances', perm: 'employees:read', icon: 'briefcase', tint: 'var(--cat-powders)' },
    { to: '/payslips', label: 'Payslips', perm: 'payroll:read', icon: 'receipt', tint: 'var(--cat-ghee)' },
    { to: '/payroll', label: 'Run Payroll', perm: 'payroll:read', icon: 'chart', tint: 'var(--cat-sweets)' },
  ] },
  { group: 'Finance', items: [
    { to: '/expenses', label: 'Expenses', perm: 'finance:read', icon: 'briefcase', tint: 'var(--cat-snacks)' },
    { to: '/finance', label: 'P&L / Month Close', perm: 'finance:read', icon: 'chart', tint: 'var(--green-2)' },
  ] },
  { group: 'Settings', items: [
    { to: '/settings', label: 'General', perm: 'settings:read', icon: 'settings', tint: 'var(--muted)' },
    { to: '/locations', label: 'Locations', perm: 'locations:read', icon: 'pin', tint: '#2a5d9e' },
    { to: '/users', label: 'Users & Roles', perm: 'users:read', icon: 'users', tint: 'var(--cat-sweets)' },
  ] },
];

const Layout = ({ children }) => {
  const { user, logout, can } = useAuth();
  const { canPick, locations, locationId, setLocationId } = useLocationFilter();
  const { pathname } = useLocation();
  const pageTitle = titleForPath(pathname, NAV);
  const allowed = (n) => !n.perm || can(n.perm);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('erp_nav_collapsed') === '1');
  const toggleNav = () => setCollapsed((c) => { const n = !c; localStorage.setItem('erp_nav_collapsed', n ? '1' : '0'); return n; });

  // Per-group expand/collapse (accordion). Default: expanded. Persisted.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('erp_nav_groups') || '{}'); } catch (e) { return {}; }
  });
  const groupOpen = (g) => openGroups[g] !== false;   // undefined = open by default
  const toggleGroup = (g) => setOpenGroups((p) => {
    const n = { ...p, [g]: !(p[g] !== false) };
    localStorage.setItem('erp_nav_groups', JSON.stringify(n));
    return n;
  });

  const NavItem = (n) => (
    <NavLink key={n.to} to={n.to} end={n.end} title={n.label} style={{ '--nav-tint': n.tint || 'var(--muted)' }}
      className={({ isActive }) => (isActive ? 'active' : '')}>
      <Icon name={n.icon} className="nav-icon" />
      <span className="nav-label">{n.label}</span>
    </NavLink>
  );

  return (
    <div className={`app-shell${collapsed ? ' nav-collapsed' : ''}`}>
      <header className="topbar no-print">
        <div className="topbar-left">
          <img src={BRAND.logo} alt={BRAND.name} className="topbar-logo" />
          <span className="brand-mark">
            <span className="brand-mark-name">{BRAND.name}</span>
            <span className="brand-mark-caption">{BRAND.system} · {BRAND.since}</span>
          </span>
          {pageTitle && <span className="topbar-page-title">{pageTitle}</span>}
          {/* Pages put a control of their own here — the date on Invoices, say —
              through <HeaderSlot>, so it sits with the page's name rather than
              taking a row of the page. */}
          <div id="topbar-slot" className="topbar-slot" />
        </div>
        <div className="topbar-user">
          {canPick && locations.length > 0 && (
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)} title="Filter data by location" style={{ width: 190 }}>
              <option value="">All locations</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <div className="tu-meta"><strong>{user?.name}</strong><span className="muted">{user?.role}</span></div>
          <button className="btn btn-outline btn-sm" onClick={logout}>Log out</button>
        </div>
      </header>
      <div className="app-body">
        <aside className="sidebar no-print">
          <nav className="sidebar-nav">
            {NAV.map((n, i) => {
              if (!n.group) return allowed(n) ? NavItem(n) : null;
              const items = n.items.filter(allowed);
              if (!items.length) return null;
              const open = groupOpen(n.group);
              return (
                <div className={`nav-group${open ? '' : ' nav-group-collapsed'}`} key={`g-${i}`}>
                  <button type="button" className="nav-group-label" onClick={() => toggleGroup(n.group)} aria-expanded={open} title={open ? 'Collapse' : 'Expand'}>
                    <span>{n.group}</span>
                    <span className="nav-group-caret">{open ? '▾' : '▸'}</span>
                  </button>
                  {open && items.map(NavItem)}
                </div>
              );
            })}
          </nav>
        </aside>
        <main className="main">
          {/* Re-key on the location filter so changing it fully remounts the
              current page — every screen reloads its data for the new location,
              even ones that don't watch the filter themselves. */}
          <div className="content" key={`${pathname}::${locationId}`}>{children}</div>
        </main>
      </div>
      <button className="nav-fab no-print" onClick={toggleNav} title={collapsed ? 'Expand menu' : 'Collapse menu'} aria-label="Toggle menu">
        <Icon name="panel" size={20} />
      </button>
    </div>
  );
};

export default Layout;
